import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { Picture } from '@/components/image/Image';
import { Product } from '@/services/api/contracts/catalog/types';
import { useSelectedFinanceModel } from '@/services/catalogue/useCustomerCatalogueService';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { FC, FunctionComponent } from 'react';

interface BundleProductTableRowProps {
  product: Product;
  count?: number;
}

export const ProductImageRow: FC<{ product: Product }> = ({ product }) => {
  return product.imageUri?.id ? (
    <div className="w-12 h-12 flex items-center justify-center shrink-0">
      <Picture image={product.imageUri} containerHeight={48} containerWidth={48}></Picture>
    </div>
  ) : (
    <div className="w-8 h-8 text-gray-300 [&_svg]:w-full [&_svg]:h-full shrink-0">
      {CategoryIcons[product.category.description]}
    </div>
  );
};

const BundleProductTableRow: FunctionComponent<BundleProductTableRowProps> = ({
  product,
  count = 1
}) => {
  const model = useSelectedFinanceModel(product.manufacturer.id, product.category.id);

  const total = (product.price?.price ?? 0) * count;

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const price = printCurrencyString(product.price?.price ?? 0);
  const totalPrice = printCurrencyString(total);

  const pcmTotal = total * (model?.pcmFactor || 0);
  const pcmCost = pcmTotal ? printCurrencyString(pcmTotal) : undefined;

  return (
    <div className="flex flex-row items-center justify-between p-2 lg:p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      <div className="flex flex-row items-center gap-4 flex-1 overflow-hidden">
        <ProductImageRow product={product} />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900 line-clamp-1" title={product.name}>
            {product.name}
          </span>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            <span>SKU: {product.code}</span>
          </div>
        </div>
      </div>
      <div className="ml-4 shrink-0 flex items-center gap-6 text-sm text-gray-700">
        <div className="w-16 text-center">{count}</div>
        <div className="w-24 text-right">{price}</div>
        <div className="w-24 text-right flex flex-col justify-end">
          <span>{totalPrice}</span>
          {pcmCost && <span className="text-xs text-gray-500 leading-tight">{`${pcmCost} pcm`}</span>}
        </div>
      </div>
    </div>
  );
};

export default BundleProductTableRow;
